import * as z from 'zod';

import { AddAccountCommentEvent } from './AddAccountCommentEvent';
import { AddAdditionalBorrowerEvent } from './AddAdditionalBorrowerEvent';
import { AddAppraisalEvent } from './AddAppraisalEvent';
import { AddAppraisalOrderEvent } from './AddAppraisalOrderEvent';
import { AddAuditLogItemEvent } from './AddAuditLogItemEvent';
import { AddInspectionEvent } from './AddInspectionEvent';
import { AddInspectionOrderEvent } from './AddInspectionOrderEvent';
import { AddLoanNoteEvent } from './AddLoanNoteEvent';
import { CancelFeeEvent } from './CancelFeeEvent';
import { ChangePrimaryBorrowerEvent } from './ChangePrimaryBorrowerEvent';
import { CloseBankruptcyEvent } from './CloseBankruptcyEvent';
import { CloseForeclosureEvent } from './CloseForeclosureEvent';
import { ConstructionToPermanentEvent } from './ConstructionToPermanentEvent';
import { CreateAdvancePaymentEvent } from './CreateAdvancePaymentEvent';
import { CreateBalanceReallocationEvent } from './CreateBalanceReallocationEvent';
import { CreateAdvanceEvent, CreateFeeEvent } from './CreateChargeEvent';
import { CreateClawBackTransferEvent } from './CreateClawBackTransferEvent';
import { CreateDepositEvent } from './CreateDepositEvent';
import { CreateDisbursementEvent } from './CreateDisbursementEvent';
import { CreateDrawEvent } from './CreateDrawEvent';
import { CreateEscrowSettingEvent } from './CreateEscrowSettingEvent';
import { CreateFeePaymentEvent } from './CreateFeePaymentEvent';
import { CreateForwardPaymentDisbursementEvent } from './CreateForwardPaymentDisbursementEvent';
import { CreateLoanExtensionAgreementEvent } from './CreateLoanExtensionAgreementEvent';
import { CreateLoanExtensionFeesDocumentEvent } from './CreateLoanExtensionFeesDocumentEvent';
import { CreateMultipleAdvancesPaymentEvent } from './CreateMultipleAdvancesPaymentEvent';
import { CreateMultipleFeesPaymentEvent } from './CreateMultipleFeesPaymentEvent';
import { CreatePaymentAfterPayoffDisbursementEvent } from './CreatePaymentAfterPayoffDisbursementEvent';
import { CreatePaymentEvent } from './CreatePaymentEvent';
import { CreatePayoffEvent } from './CreatePayoffEvent';
import { CreatePrincipalModificationEvent } from './CreatePrincipalModificationEvent';
import { CreatePrincipalPrepaymentEvent } from './CreatePrincipalPrepaymentEvent';
import { CreatePropertyAddressEvent } from './CreatePropertyAddressEvent';
import { CreateRequestedPayoffEvent } from './CreateRequestedPayoffEvent';
import { CreateTransferEvent } from './CreateTransferEvent';
import { CreateVerificationOfMortgageEvent } from './CreateVerificationOfMortgageEvent';
import { CreationEvent } from './CreationEvent';
import { DeferDelinquentInterestEvent } from './DeferDelinquentInterestEvent';
import { DeleteDocumentEvent } from './DeleteDocumentEvent';
import { DeletePropertyAddressEvent } from './DeletePropertyAddressEvent';
import { DeleteReportableEventEvent } from './DeleteReportableEventEvent';
import { DeriveLedgerEvent } from './DeriveLedgerEvent';
import { EditBorrowerEvent } from './EditBorrowerEvent';
import { EditEscrowFieldsEvent } from './EditEscrowFieldsEvent';
import { EditManualStatementEvent } from './EditManualStatementEvent';
import { FlagAccountEvent } from './FlagAccountEvent';
import { ForceUpdateEvent } from './ForceUpdateEvent';
import { GenerateGoodbyeLetterPdfEvent } from './GenerateGoodbyeLetterPdfEvent';
import { GenerateManualStatementEvent } from './GenerateManualStatementEvent';
import { GenerateNextStatementEvent } from './GenerateNextStatementEvent';
import { GracePastDueAutomationEvent } from './GracePastDueAutomationEvent';
import { LogCallEvent } from './LogCallEvent';
import { MailMonthlyStatementTaskEvent } from './MailMonthlyStatementTaskEvent';
import { ModifyMaturityDateEvent } from './ModifyMaturityDateEvent';
import { ModifyPropertyAddressEvent } from './ModifyPropertyAddressEvent';
import { MoveToTransferEvent } from './MoveToTransferEvent';
import { NachaMarkAsCancelledEvent } from './NachaMarkAsCancelledEvent';
import { NachaMarkAsFailedEvent } from './NachaMarkAsFailedEvent';
import { NachaMarkAsProcessedEvent } from './NachaMarkAsProcessedEvent';
import { NextAmortizationMonthEvent } from './NextAmortizationMonthEvent';
import { LiquidateLoanEvent } from './NonPerformance/LiquidateLoanEvent';
import { RecordLiquidationEvent } from './NonPerformance/RecordLiquidationEvent';
import { ReverseLiquidationEvent } from './NonPerformance/ReverseLiquidationEvent';
import { PauseForeclosureEvent } from './PauseForeclosureEvent';
import { PrincipalLateAutomationEvent } from './PrincipalLateAutomationEvent';
import { ReallocatePaymentsEvent } from './ReallocatePaymentsEvent';
import { RecordPayoffEvent } from './RecordPayoffEvent';
import { RecordReoEvent } from './RecordReoEvent';
import { RecordWriteoffEvent } from './RecordWriteoffEvent';
import { RegenerateStatementEvent } from './RegenerateStatementEvent';
import { RegenerateStatementPdfEvent } from './RegenerateStatementPdfEvent';
import { RegenerateTransferDocumentsEvent } from './RegenerateTransferDocumentsEvent';
import { RemoveAdditionalBorrowerEvent } from './RemoveAdditionalBorrowerEvent';
import { ReopenForeclosureEvent } from './ReopenForeclosureEvent';
import { RepayMarkAsCancelledEvent } from './RepayMarkAsCancelledEvent';
import { RepayMarkAsFailedEvent } from './RepayMarkAsFailedEvent';
import { RepayMarkAsProcessedEvent } from './RepayMarkAsProcessedEvent';
import { ReverseDisbursementEvent } from './ReverseDisbursementEvent';
import { ReverseDrawEvent } from './ReverseDrawEvent';
import { ReversePaymentEvent } from './ReversePaymentEvent';
import { ReversePayoffEvent } from './ReversePayoffEvent';
import { ReversePrincipalModificationEvent } from './ReversePrincipalModificationEvent';
import { SaveDocumentPdfEvent } from './SaveDocumentPdfEvent';
import { SaveStatementPdfEvent } from './SaveStatementPdfEvent';
import { SendEmailEvent } from './SendEmailEvent';
import { SendEscrowCompanyEmailsEvent } from './SendEscrowCompanyEmailsEvent';
import { SendExternalEmailEvent } from './SendExternalEmailEvent';
import { SendFinalPaymentEmailEvent } from './SendFinalPaymentEmailEvent';
import { SendGoodbyeEmailEvent } from './SendGoodbyeEmailEvent';
import { SendGracePastDueEmailEvent } from './SendGracePastDueEmailEvent';
import { SendLatePaymentEmailEvent } from './SendLatePaymentEmailEvent';
import { SendLenderEmailEvent } from './SendLenderEmailEvent';
import { SendMortgageeLetterEvent } from './SendMortgageeLetterEvent';
import { SendPaymentReminderEmailEvent } from './SendPaymentReminderEmailEvent';
import { SendWelcomeEmailEvent } from './SendWelcomeEmailEvent';
import { SetLoanStatusEvent } from './SetLoanStatusEvent';
import { TransferAccountEvent } from './TransferAccountEvent';
import { UpdateAdvanceInterestEvent } from './UpdateAdvanceInterestEvent';
import { UpdateAutopaySettingsEvent } from './UpdateAutopaySettingsEvent';
import { UpdateBillSettingsEvent } from './UpdateBillSettingsEvent';
import { UpdateDelinquentInterestRateEvent } from './UpdateDelinquentInterestRateEvent';
import { UpdateDocumentBorrowerAccessEvent } from './UpdateDocumentBorrowerAccess';
import { UpdateDocumentTitleEvent } from './UpdateDocumentTitleEvent';
import { UpdateEscrowCompanyEvent } from './UpdateEscrowCompanyEvent';
import { UpdateEscrowDueEvent } from './UpdateEscrowDueEvent';
import { UpdateEscrowPaymentEvent } from './UpdateEscrowPaymentEvent';
import { UpdateEscrowSettingEvent } from './UpdateEscrowSettingEvent';
import { UpdateFeesEvent } from './UpdateFeesEvent';
import { UpdateIdsAndRemittanceDataEvent } from './UpdateIdsAndRemittanceDataEvent';
import { UpdateLoanBoardedDatesEvent } from './UpdateLoanBoardedDatesEvent';
import { UpdateLoanEvent } from './UpdateLoanEvent';
import { UpdateMonthlyInterestRateEvent } from './UpdateMonthlyInterestRateEvent';
import { UpdateNoteflowDocumentIdEvent } from './UpdateNoteflowDocumentIdEvent';
import { UpdatePaymentEvent } from './UpdatePaymentEvent';
import { UpdateRemittanceSplitEvent } from './UpdateRemittanceSplitEvent';
import { UpdateSentEmailEvent } from './UpdateSentEmailEvent';
import { UpdateStopSettingsEvent } from './UpdateStopSettingsEvent';
import { UpdateTransferDataEvent } from './UpdateTransferDataEvent';
import { UpdateTransferEvent } from './UpdateTransferEvent';
import { UpsertBankruptcyEvent } from './UpsertBankruptcyEvent';
import { UpsertForeclosureEvent } from './UpsertForeclosureEvent';
import { UpsertReportableEvent } from './UpsertReportableEvent';
import { UsioMarkAsCancelledEvent } from './UsioMarkAsCancelledEvent';
import { UsioMarkAsFailedEvent } from './UsioMarkAsFailedEvent';
import { UsioMarkAsProcessedEvent } from './UsioMarkAsProcessedEvent';
import { UsioTransactionBatchedEvent } from './UsioTransactionBatchedEvent';
import { WaiveDelinquentInterestEvent } from './WaiveDelinquentInterestEvent';
import { ArchiveWorkoutPlanEvent } from './WorkoutPlans/ArchiveWorkoutPlanEvent';
import { CancelWorkoutPlanEvent } from './WorkoutPlans/CancelWorkoutPlanEvent';
import { CreateWorkoutPlanEvent } from './WorkoutPlans/CreateWorkoutPlanEvent';
import { CompleteFlexModWorkoutPlanEvent } from './WorkoutPlans/FlexMod/CompleteFlexModWorkoutPlanEvent';
import { CreateWorkoutPlanFlexModEvent } from './WorkoutPlans/FlexMod/CreateWorkoutPlanFlexModEvent';
import { FlexModCancellationEvent } from './WorkoutPlans/FlexMod/FlexModCancellationEvent';
import { CancelForbearanceWorkoutPlanEvent } from './WorkoutPlans/Forbearance/CancelForbearanceWorkoutPlanEvent';
import { CompleteForbearanceWorkoutPlanEvent } from './WorkoutPlans/Forbearance/CompleteForbearanceWorkoutPlanEvent';
import { CreateWorkoutPlanForbearanceEvent } from './WorkoutPlans/Forbearance/CreateWorkoutPlanForbearanceEvent';
import { ExtendForbearanceWorkoutPlanEvent } from './WorkoutPlans/Forbearance/ExtendForbearanceWorkoutPlanEvent';
import { ScheduleForbearanceWorkoutPlanCancellationEvent } from './WorkoutPlans/Forbearance/ScheduleForbearanceWorkoutPlanCancellationEvent';
import { ActivatePaymentDeferralWorkoutPlanEvent } from './WorkoutPlans/PaymentDeferral/ActivatePaymentDeferralWorkoutPlanEvent';
import { CancelPaymentDeferralWorkoutPlanEvent } from './WorkoutPlans/PaymentDeferral/CancelPaymentDeferralWorkoutPlanEvent';
import { CreatePaymentDeferralWorkoutPlanEvent } from './WorkoutPlans/PaymentDeferral/CreatePaymentDeferralWorkoutPlanEvent';
import { CancelRepaymentWorkoutPlanEvent } from './WorkoutPlans/RepaymentPlan/CancelRepaymentWorkoutPlanEvent';
import { CompleteRepaymentWorkoutPlanEvent } from './WorkoutPlans/RepaymentPlan/CompleteRepaymentWorkoutPlanEvent';
import { CreateRepaymentWorkoutPlanEvent } from './WorkoutPlans/RepaymentPlan/CreateRepaymentWorkoutPlanEvent';

// LoanEvent's discriminated union has ~140 variants and one of them (UpsertReportableEvent)
// transitively references ReportableEventInput, which is itself a discriminated union with
// per-variant deeply-conditional types from `inputOf<T>` in ./loan/ReportableEvent.ts.
// Letting TS infer the const's type pushes the serialized form past TS7056. We declare the
// type alias up front and use an explicit `z.ZodType<LoanEvent>` annotation on the const to
// short-circuit the inference.
export type LoanEvent =
  | z.infer<typeof ActivatePaymentDeferralWorkoutPlanEvent>
  | z.infer<typeof AddAccountCommentEvent>
  | z.infer<typeof AddAdditionalBorrowerEvent>
  | z.infer<typeof AddAppraisalEvent>
  | z.infer<typeof AddAppraisalOrderEvent>
  | z.infer<typeof AddAuditLogItemEvent>
  | z.infer<typeof AddInspectionEvent>
  | z.infer<typeof AddInspectionOrderEvent>
  | z.infer<typeof AddLoanNoteEvent>
  | z.infer<typeof ArchiveWorkoutPlanEvent>
  | z.infer<typeof CancelFeeEvent>
  | z.infer<typeof CancelForbearanceWorkoutPlanEvent>
  | z.infer<typeof CancelPaymentDeferralWorkoutPlanEvent>
  | z.infer<typeof CancelRepaymentWorkoutPlanEvent>
  | z.infer<typeof CancelWorkoutPlanEvent>
  | z.infer<typeof ChangePrimaryBorrowerEvent>
  | z.infer<typeof CloseBankruptcyEvent>
  | z.infer<typeof CloseForeclosureEvent>
  | z.infer<typeof CompleteFlexModWorkoutPlanEvent>
  | z.infer<typeof CompleteForbearanceWorkoutPlanEvent>
  | z.infer<typeof CompleteRepaymentWorkoutPlanEvent>
  | z.infer<typeof ConstructionToPermanentEvent>
  | z.infer<typeof CreateAdvanceEvent>
  | z.infer<typeof CreateAdvancePaymentEvent>
  | z.infer<typeof CreateBalanceReallocationEvent>
  | z.infer<typeof CreateClawBackTransferEvent>
  | z.infer<typeof CreateDepositEvent>
  | z.infer<typeof CreateDisbursementEvent>
  | z.infer<typeof CreateDrawEvent>
  | z.infer<typeof CreateEscrowSettingEvent>
  | z.infer<typeof CreateFeeEvent>
  | z.infer<typeof CreateFeePaymentEvent>
  | z.infer<typeof CreateForwardPaymentDisbursementEvent>
  | z.infer<typeof CreateLoanExtensionAgreementEvent>
  | z.infer<typeof CreateLoanExtensionFeesDocumentEvent>
  | z.infer<typeof CreateMultipleAdvancesPaymentEvent>
  | z.infer<typeof CreateMultipleFeesPaymentEvent>
  | z.infer<typeof CreatePaymentAfterPayoffDisbursementEvent>
  | z.infer<typeof CreatePaymentDeferralWorkoutPlanEvent>
  | z.infer<typeof CreatePaymentEvent>
  | z.infer<typeof CreatePayoffEvent>
  | z.infer<typeof CreatePrincipalModificationEvent>
  | z.infer<typeof CreatePrincipalPrepaymentEvent>
  | z.infer<typeof CreatePropertyAddressEvent>
  | z.infer<typeof CreateRepaymentWorkoutPlanEvent>
  | z.infer<typeof CreateRequestedPayoffEvent>
  | z.infer<typeof CreateTransferEvent>
  | z.infer<typeof CreateVerificationOfMortgageEvent>
  | z.infer<typeof CreateWorkoutPlanEvent>
  | z.infer<typeof CreateWorkoutPlanFlexModEvent>
  | z.infer<typeof CreateWorkoutPlanForbearanceEvent>
  | z.infer<typeof CreationEvent>
  | z.infer<typeof DeferDelinquentInterestEvent>
  | z.infer<typeof DeleteDocumentEvent>
  | z.infer<typeof DeletePropertyAddressEvent>
  | z.infer<typeof DeleteReportableEventEvent>
  | z.infer<typeof DeriveLedgerEvent>
  | z.infer<typeof EditBorrowerEvent>
  | z.infer<typeof EditEscrowFieldsEvent>
  | z.infer<typeof EditManualStatementEvent>
  | z.infer<typeof ExtendForbearanceWorkoutPlanEvent>
  | z.infer<typeof FlagAccountEvent>
  | z.infer<typeof FlexModCancellationEvent>
  | z.infer<typeof ForceUpdateEvent>
  | z.infer<typeof GenerateGoodbyeLetterPdfEvent>
  | z.infer<typeof GenerateManualStatementEvent>
  | z.infer<typeof GenerateNextStatementEvent>
  | z.infer<typeof GracePastDueAutomationEvent>
  | z.infer<typeof LiquidateLoanEvent>
  | z.infer<typeof LogCallEvent>
  | z.infer<typeof MailMonthlyStatementTaskEvent>
  | z.infer<typeof ModifyMaturityDateEvent>
  | z.infer<typeof ModifyPropertyAddressEvent>
  | z.infer<typeof MoveToTransferEvent>
  | z.infer<typeof NachaMarkAsCancelledEvent>
  | z.infer<typeof NachaMarkAsFailedEvent>
  | z.infer<typeof NachaMarkAsProcessedEvent>
  | z.infer<typeof NextAmortizationMonthEvent>
  | z.infer<typeof PauseForeclosureEvent>
  | z.infer<typeof PrincipalLateAutomationEvent>
  | z.infer<typeof ReallocatePaymentsEvent>
  | z.infer<typeof RecordLiquidationEvent>
  | z.infer<typeof RecordPayoffEvent>
  | z.infer<typeof RecordReoEvent>
  | z.infer<typeof RecordWriteoffEvent>
  | z.infer<typeof RegenerateStatementEvent>
  | z.infer<typeof RegenerateStatementPdfEvent>
  | z.infer<typeof RegenerateTransferDocumentsEvent>
  | z.infer<typeof RemoveAdditionalBorrowerEvent>
  | z.infer<typeof ReopenForeclosureEvent>
  | z.infer<typeof RepayMarkAsCancelledEvent>
  | z.infer<typeof RepayMarkAsFailedEvent>
  | z.infer<typeof RepayMarkAsProcessedEvent>
  | z.infer<typeof ReverseDisbursementEvent>
  | z.infer<typeof ReverseDrawEvent>
  | z.infer<typeof ReverseLiquidationEvent>
  | z.infer<typeof ReversePaymentEvent>
  | z.infer<typeof ReversePayoffEvent>
  | z.infer<typeof ReversePrincipalModificationEvent>
  | z.infer<typeof SaveDocumentPdfEvent>
  | z.infer<typeof SaveStatementPdfEvent>
  | z.infer<typeof ScheduleForbearanceWorkoutPlanCancellationEvent>
  | z.infer<typeof SendEmailEvent>
  | z.infer<typeof SendEscrowCompanyEmailsEvent>
  | z.infer<typeof SendExternalEmailEvent>
  | z.infer<typeof SendFinalPaymentEmailEvent>
  | z.infer<typeof SendGoodbyeEmailEvent>
  | z.infer<typeof SendGracePastDueEmailEvent>
  | z.infer<typeof SendLatePaymentEmailEvent>
  | z.infer<typeof SendLenderEmailEvent>
  | z.infer<typeof SendMortgageeLetterEvent>
  | z.infer<typeof SendPaymentReminderEmailEvent>
  | z.infer<typeof SendWelcomeEmailEvent>
  | z.infer<typeof SetLoanStatusEvent>
  | z.infer<typeof TransferAccountEvent>
  | z.infer<typeof UpdateAdvanceInterestEvent>
  | z.infer<typeof UpdateAutopaySettingsEvent>
  | z.infer<typeof UpdateBillSettingsEvent>
  | z.infer<typeof UpdateDelinquentInterestRateEvent>
  | z.infer<typeof UpdateDocumentBorrowerAccessEvent>
  | z.infer<typeof UpdateDocumentTitleEvent>
  | z.infer<typeof UpdateEscrowCompanyEvent>
  | z.infer<typeof UpdateEscrowDueEvent>
  | z.infer<typeof UpdateEscrowPaymentEvent>
  | z.infer<typeof UpdateEscrowSettingEvent>
  | z.infer<typeof UpdateFeesEvent>
  | z.infer<typeof UpdateIdsAndRemittanceDataEvent>
  | z.infer<typeof UpdateLoanBoardedDatesEvent>
  | z.infer<typeof UpdateLoanEvent>
  | z.infer<typeof UpdateMonthlyInterestRateEvent>
  | z.infer<typeof UpdateNoteflowDocumentIdEvent>
  | z.infer<typeof UpdatePaymentEvent>
  | z.infer<typeof UpdateRemittanceSplitEvent>
  | z.infer<typeof UpdateSentEmailEvent>
  | z.infer<typeof UpdateStopSettingsEvent>
  | z.infer<typeof UpdateTransferDataEvent>
  | z.infer<typeof UpdateTransferEvent>
  | z.infer<typeof UpsertBankruptcyEvent>
  | z.infer<typeof UpsertForeclosureEvent>
  | z.infer<typeof UpsertReportableEvent>
  | z.infer<typeof UsioMarkAsCancelledEvent>
  | z.infer<typeof UsioMarkAsFailedEvent>
  | z.infer<typeof UsioMarkAsProcessedEvent>
  | z.infer<typeof UsioTransactionBatchedEvent>
  | z.infer<typeof WaiveDelinquentInterestEvent>;

export const LoanEvent: z.ZodType<LoanEvent> = z.discriminatedUnion('type', [
  FlagAccountEvent,
  ForceUpdateEvent,
  CreationEvent,
  TransferAccountEvent,
  CreatePaymentEvent,
  CreateDepositEvent,
  CreateBalanceReallocationEvent,
  CreateFeePaymentEvent,
  CreateMultipleFeesPaymentEvent,
  CreateAdvancePaymentEvent,
  CreateMultipleAdvancesPaymentEvent,
  CreatePrincipalPrepaymentEvent,
  CreateTransferEvent,
  CreateClawBackTransferEvent,
  UpdatePaymentEvent,
  UpdateTransferEvent,
  ReversePaymentEvent,
  ReversePayoffEvent,
  GenerateManualStatementEvent,
  EditManualStatementEvent,
  GenerateNextStatementEvent,
  NextAmortizationMonthEvent,
  CreateDisbursementEvent,
  CreateForwardPaymentDisbursementEvent,
  CreatePaymentAfterPayoffDisbursementEvent,
  ReverseDisbursementEvent,
  SendGoodbyeEmailEvent,
  SendMortgageeLetterEvent,
  SendWelcomeEmailEvent,
  MoveToTransferEvent,
  SendPaymentReminderEmailEvent,
  SendLatePaymentEmailEvent,
  SendGracePastDueEmailEvent,
  GracePastDueAutomationEvent,
  SendLenderEmailEvent,
  AddAdditionalBorrowerEvent,
  RemoveAdditionalBorrowerEvent,
  EditBorrowerEvent,
  ChangePrimaryBorrowerEvent,
  SaveStatementPdfEvent,
  UpdateDocumentTitleEvent,
  UpdateDocumentBorrowerAccessEvent,
  DeleteDocumentEvent,
  SendEscrowCompanyEmailsEvent,
  SendFinalPaymentEmailEvent,
  RegenerateStatementEvent,
  RegenerateStatementPdfEvent,
  RegenerateTransferDocumentsEvent,
  AddAccountCommentEvent, // deprecated
  CreateFeeEvent,
  CancelFeeEvent,
  DeriveLedgerEvent,
  UpdateFeesEvent,
  CreateDrawEvent,
  UpdateAutopaySettingsEvent,
  ReverseDrawEvent,
  EditEscrowFieldsEvent,
  ModifyMaturityDateEvent,
  ModifyPropertyAddressEvent,
  CreatePropertyAddressEvent,
  DeletePropertyAddressEvent,
  AddAuditLogItemEvent,
  CreateRequestedPayoffEvent,
  CreatePayoffEvent,
  RecordPayoffEvent,
  UpdateAdvanceInterestEvent,
  CreateAdvanceEvent,
  UpdateMonthlyInterestRateEvent,
  GenerateGoodbyeLetterPdfEvent,
  UpdateEscrowSettingEvent,
  UpdateEscrowCompanyEvent,
  UpdateEscrowDueEvent,
  CreateEscrowSettingEvent,
  NachaMarkAsCancelledEvent,
  NachaMarkAsFailedEvent,
  NachaMarkAsProcessedEvent,
  UsioMarkAsCancelledEvent,
  UsioMarkAsFailedEvent,
  UsioMarkAsProcessedEvent,
  UsioTransactionBatchedEvent,
  UpdateIdsAndRemittanceDataEvent,
  UpdateLoanEvent,
  UpdateLoanBoardedDatesEvent,
  ConstructionToPermanentEvent,
  CreateWorkoutPlanEvent,
  CancelWorkoutPlanEvent,
  ArchiveWorkoutPlanEvent,
  CreateWorkoutPlanForbearanceEvent,
  CreateWorkoutPlanFlexModEvent,
  ExtendForbearanceWorkoutPlanEvent,
  ScheduleForbearanceWorkoutPlanCancellationEvent,
  CancelForbearanceWorkoutPlanEvent,
  CompleteForbearanceWorkoutPlanEvent,
  FlexModCancellationEvent,
  CompleteFlexModWorkoutPlanEvent,
  CreateRepaymentWorkoutPlanEvent,
  CancelRepaymentWorkoutPlanEvent,
  CompleteRepaymentWorkoutPlanEvent,
  CreatePaymentDeferralWorkoutPlanEvent,
  ActivatePaymentDeferralWorkoutPlanEvent,
  CancelPaymentDeferralWorkoutPlanEvent,
  UpdateDelinquentInterestRateEvent,
  UpdateRemittanceSplitEvent,
  WaiveDelinquentInterestEvent,
  DeferDelinquentInterestEvent,
  UpdateBillSettingsEvent,
  MailMonthlyStatementTaskEvent,
  UpdateTransferDataEvent,
  AddAppraisalEvent,
  AddAppraisalOrderEvent,
  AddInspectionEvent,
  AddInspectionOrderEvent,
  UpdateNoteflowDocumentIdEvent,
  CreatePrincipalModificationEvent,
  ReversePrincipalModificationEvent,
  RecordWriteoffEvent,
  RepayMarkAsProcessedEvent,
  RepayMarkAsCancelledEvent,
  RepayMarkAsFailedEvent,
  CreateVerificationOfMortgageEvent,
  CreateLoanExtensionAgreementEvent,
  PrincipalLateAutomationEvent,
  CreateLoanExtensionFeesDocumentEvent,
  UpsertBankruptcyEvent,
  CloseBankruptcyEvent,
  LogCallEvent,
  AddLoanNoteEvent,
  ReallocatePaymentsEvent,
  UpsertReportableEvent,
  DeleteReportableEventEvent,
  UpdateStopSettingsEvent,

  // LOAN STATUS
  SetLoanStatusEvent,
  RecordReoEvent,
  RecordLiquidationEvent,
  LiquidateLoanEvent,
  ReverseLiquidationEvent,
  UpsertForeclosureEvent,
  CloseForeclosureEvent,
  ReopenForeclosureEvent,
  PauseForeclosureEvent,

  // nested
  SendEmailEvent,
  UpdateSentEmailEvent,
  SendExternalEmailEvent,
  SaveDocumentPdfEvent,
  UpdateEscrowPaymentEvent,
]);

export const LoanEventWithAuditLog = z.discriminatedUnion('type', [
  FlagAccountEvent,
  ReverseDisbursementEvent,
  CreationEvent,
  TransferAccountEvent,
  CreatePaymentEvent,
  ReversePaymentEvent,
  ReversePayoffEvent,
  GenerateManualStatementEvent,
  EditManualStatementEvent,
  GenerateNextStatementEvent,
  SaveDocumentPdfEvent,
  DeleteDocumentEvent,
  UpdateDocumentTitleEvent,
  UpdateDocumentBorrowerAccessEvent,
  CreateDisbursementEvent,
  CreateForwardPaymentDisbursementEvent,
  CreatePaymentAfterPayoffDisbursementEvent,
  MoveToTransferEvent,
  AddAdditionalBorrowerEvent,
  RemoveAdditionalBorrowerEvent,
  ChangePrimaryBorrowerEvent,
  AddAccountCommentEvent, // deprecated
  RegenerateStatementEvent,
  RegenerateStatementPdfEvent,
  CreateFeeEvent,
  CancelFeeEvent,
  DeriveLedgerEvent,
  UpdateFeesEvent,
  EditBorrowerEvent,
  CreateDrawEvent,
  ReverseDrawEvent,
  UpdateAutopaySettingsEvent,
  EditEscrowFieldsEvent,
  ModifyMaturityDateEvent,
  ModifyPropertyAddressEvent,
  CreatePropertyAddressEvent,
  DeletePropertyAddressEvent,
  AddAuditLogItemEvent,
  CreateRequestedPayoffEvent,
  CreatePayoffEvent,
  RecordPayoffEvent,
  CreateAdvanceEvent,
  UpdateMonthlyInterestRateEvent,
  UpdateEscrowSettingEvent,
  UpdateEscrowCompanyEvent,
  UpdateEscrowDueEvent,
  CreateEscrowSettingEvent,
  CreateFeePaymentEvent,
  CreateMultipleFeesPaymentEvent,
  CreateAdvancePaymentEvent,
  CreateMultipleAdvancesPaymentEvent,
  UpdateEscrowPaymentEvent,
  UpdateIdsAndRemittanceDataEvent,
  UpdateLoanEvent,
  UpdateLoanBoardedDatesEvent,
  ConstructionToPermanentEvent,
  CreateWorkoutPlanEvent,
  CancelWorkoutPlanEvent,
  ArchiveWorkoutPlanEvent,
  CreateWorkoutPlanForbearanceEvent,
  ExtendForbearanceWorkoutPlanEvent,
  CompleteForbearanceWorkoutPlanEvent,
  ScheduleForbearanceWorkoutPlanCancellationEvent,
  CancelForbearanceWorkoutPlanEvent,
  CreateWorkoutPlanFlexModEvent,
  FlexModCancellationEvent,
  CompleteFlexModWorkoutPlanEvent,
  CreateRepaymentWorkoutPlanEvent,
  CancelRepaymentWorkoutPlanEvent,
  CompleteRepaymentWorkoutPlanEvent,
  CreatePaymentDeferralWorkoutPlanEvent,
  ActivatePaymentDeferralWorkoutPlanEvent,
  CancelPaymentDeferralWorkoutPlanEvent,
  UpdateRemittanceSplitEvent,
  UpdateDelinquentInterestRateEvent,
  WaiveDelinquentInterestEvent,
  DeferDelinquentInterestEvent,
  UpdateBillSettingsEvent,
  UpdateTransferDataEvent,
  CreateDepositEvent,
  CreateBalanceReallocationEvent,
  AddAppraisalEvent,
  AddAppraisalOrderEvent,
  AddInspectionEvent,
  AddInspectionOrderEvent,
  CreatePrincipalModificationEvent,
  ReversePrincipalModificationEvent,
  RecordWriteoffEvent,
  CreateVerificationOfMortgageEvent,
  CreateLoanExtensionAgreementEvent,
  CreateLoanExtensionFeesDocumentEvent,
  ReallocatePaymentsEvent,
  UpdateStopSettingsEvent,
  UpsertReportableEvent,
  DeleteReportableEventEvent,
  AddLoanNoteEvent,
  LogCallEvent,

  // LOAN STATUS
  SetLoanStatusEvent,
  UpsertBankruptcyEvent,
  CloseBankruptcyEvent,
  RecordReoEvent,
  RecordLiquidationEvent,
  LiquidateLoanEvent,
  ReverseLiquidationEvent,
  UpsertForeclosureEvent,
  CloseForeclosureEvent,
  ReopenForeclosureEvent,
  PauseForeclosureEvent,
]);
export type LoanEventWithAuditLog = z.infer<typeof LoanEventWithAuditLog>;
